import { Component } from '@theme/component';

/**
 * Makes an overflow container drag-scrollable with a mouse.
 *
 * Touch and trackpad already scroll these natively, so this only handles
 * pointerType 'mouse'. Claiming touch as well would replace momentum scrolling
 * and rubber-banding with a worse hand-rolled version of both.
 *
 * The scroller is marked with [data-drag-scroll] rather than assumed to be the
 * first child, so controls can sit inside this element — which they must, to
 * bind to it — without changing which element scrolls.
 */
class DcrDragScroll extends Component {
  /** @type {HTMLElement | null} */
  #scroller = null;
  #startX = 0;
  #startScroll = 0;
  #dragging = false;
  #moved = false;

  /**
   * With [data-require-reduced-motion] the component only activates when the
   * visitor has asked for reduced motion. The About strip uses that: it
   * marquees normally, and becomes a manually scrolled carousel when the
   * animation is suppressed. Dragging a strip that is also moving on its own
   * is two behaviours fighting, so it stays off the rest of the time.
   */
  get #shouldActivate() {
    if (!this.hasAttribute('data-require-reduced-motion')) return true;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.#shouldActivate) return;

    this.#scroller = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-drag-scroll]') ?? this.firstElementChild
    );
    if (!this.#scroller) return;

    this.#scroller.addEventListener('scroll', this.#syncControls, { passive: true });
    this.#syncControls();

    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);

    // Capture phase: a drag that ends over a button must not also press it.
    this.addEventListener('click', this.#onClick, true);

    // Without this the browser's own image/link drag starts the moment the
    // pointer moves over a tile, which cancels the pointer stream and leaves
    // the row stuck — the reason a mouse drag appeared to do nothing.
    this.addEventListener('dragstart', this.#onDragStart);

    // Non-passive: turning a vertical wheel into horizontal scroll means
    // preventing the default vertical one.
    this.addEventListener('wheel', this.#onWheel, { passive: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerup', this.#onPointerUp);
    this.removeEventListener('pointercancel', this.#onPointerUp);
    this.removeEventListener('click', this.#onClick, true);
    this.removeEventListener('dragstart', this.#onDragStart);
    this.removeEventListener('wheel', this.#onWheel);
    this.#scroller?.removeEventListener('scroll', this.#syncControls);
  }

  /** @param {Event} event */
  #onDragStart = (event) => {
    event.preventDefault();
  };

  /**
   * Sends a vertical wheel sideways, so the row scrolls under the cursor
   * without the drawer moving behind it.
   * @param {WheelEvent} event
   */
  #onWheel = (event) => {
    const scroller = this.#scroller;
    if (!scroller) return;

    const max = scroller.scrollWidth - scroller.clientWidth;
    if (max <= 1) return;

    // A trackpad sending deltaX already means horizontal; leave it native.
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

    // At either end, hand the gesture back so the drawer scrolls instead of
    // the wheel dying over a row that cannot move any further.
    if (event.deltaY < 0 && scroller.scrollLeft <= 0) return;
    if (event.deltaY > 0 && scroller.scrollLeft >= max - 1) return;

    event.preventDefault();
    scroller.scrollLeft += event.deltaY;
  };

  /** One tile plus the gap, measured rather than assumed. */
  get #step() {
    const item = this.#scroller?.firstElementChild;
    if (!(item instanceof HTMLElement) || !this.#scroller) return 0;

    const gap = parseFloat(getComputedStyle(this.#scroller).columnGap) || 0;
    return item.offsetWidth + gap;
  }

  /** @param {Event} [event] */
  next = (event) => {
    event?.preventDefault();
    this.#scroller?.scrollBy({ left: this.#step, behavior: 'smooth' });
  };

  /** @param {Event} [event] */
  previous = (event) => {
    event?.preventDefault();
    this.#scroller?.scrollBy({ left: -this.#step, behavior: 'smooth' });
  };

  /**
   * Disables an arrow at the end it points to. A control that stays enabled
   * with nothing left to reach reads as broken rather than as finished.
   */
  #syncControls = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    // A pixel of tolerance: fractional widths mean scrollLeft rarely lands
    // exactly on the maximum.
    const max = scroller.scrollWidth - scroller.clientWidth;
    const atStart = scroller.scrollLeft <= 1;
    const atEnd = scroller.scrollLeft >= max - 1;

    for (const button of this.querySelectorAll('[data-drag-scroll-previous]')) {
      if (button instanceof HTMLButtonElement) button.disabled = atStart;
    }
    for (const button of this.querySelectorAll('[data-drag-scroll-next]')) {
      if (button instanceof HTMLButtonElement) button.disabled = atEnd;
    }

    this.toggleAttribute('data-scrollable', max > 1);
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !this.#scroller) return;

    this.#dragging = true;
    this.#moved = false;
    this.#startX = event.clientX;
    this.#startScroll = this.#scroller.scrollLeft;
    this.dataset.dragging = 'true';
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#dragging || !this.#scroller) return;

    const delta = event.clientX - this.#startX;

    // A few pixels of slop, so a slightly imprecise click is still a click.
    if (!this.#moved && Math.abs(delta) < 4) return;

    if (!this.#moved) {
      this.#moved = true;
      // Snapping fights the pointer while dragging; it is restored on release
      // so the row still settles onto a tile.
      this.#scroller.style.scrollSnapType = 'none';
      this.setPointerCapture(event.pointerId);
    }

    event.preventDefault();
    this.#scroller.scrollLeft = this.#startScroll - delta;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#dragging) return;

    this.#dragging = false;
    delete this.dataset.dragging;

    if (this.#scroller) this.#scroller.style.scrollSnapType = '';
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!this.#moved) return;

    // Swallow the click the drag would otherwise deliver to a quick add button
    // or a product link underneath the cursor.
    event.preventDefault();
    event.stopPropagation();
    this.#moved = false;
  };
}

if (!customElements.get('dcr-drag-scroll')) {
  customElements.define('dcr-drag-scroll', DcrDragScroll);
}
